/** UXP-00011 — Financial Intelligence Platform framework (mirrors pkg/financialintel) */

export const UXP_FI_ID = 'UXP-00011';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface SettlementRow {
  id: string;
  expected: string;
  actual: string;
  variance: string;
  status: string;
}

export interface ProfitabilityRow {
  level: string;
  name: string;
  margin: string;
  roi: string;
}

export interface FeeRow {
  type: string;
  amount: string;
  pct: string;
  leak: boolean;
}

export interface CashFlowRow {
  label: string;
  current: string;
  forecast: string;
}

export interface GSTRow {
  return: string;
  period: string;
  status: string;
  dueDate: string;
}

export interface ReconciliationRow {
  type: string;
  matched: number;
  exceptions: number;
  status: string;
}

export interface CFORec {
  type: string;
  title: string;
  impact: string;
  confidence: number;
}

export const COMMAND_CENTER_METRICS = [
  'Revenue', 'Gross Profit', 'Net Profit', 'EBITDA', 'Cash Position',
  'Working Capital', 'Settlement Health', 'Business Health', 'AI Confidence',
];

export const SETTLEMENT_FEATURES = [
  'Settlement Timeline', 'Expected Settlement', 'Actual Settlement', 'Reserve Balance',
  'Claims', 'Adjustments', 'Reversals', 'Settlement Health',
];

export const FEE_TYPES = [
  'Marketplace Fees', 'FBA Fees', 'Storage Fees', 'Advertising Cost',
  'Returns Cost', 'Refund Fees', 'Hidden Fees', 'Fee Leakage',
];

export const PROFITABILITY_LEVELS = [
  'Product Profitability', 'SKU Profitability', 'ASIN Profitability', 'Brand Profitability',
  'Marketplace Profitability', 'Customer Profitability', 'Advertising Profitability',
  'Contribution Margin', 'ROI',
];

export const COST_CATEGORIES = [
  'COGS', 'Packaging', 'Shipping', 'Warehousing', 'Advertising',
  'Marketplace Fees', 'GST', 'Operational Expenses', 'Custom Cost Categories',
];

export const CASH_FLOW_METRICS = [
  'Cash Position', 'Working Capital', 'Receivables', 'Payables',
  'Supplier Payments', 'GST Liability', 'Funding Needs', 'Liquidity Risk',
];

export const GST_FEATURES = [
  'GSTR-1', 'GSTR-3B', 'GSTR-6', 'HSN Summary', 'TCS', 'TDS',
  'Input Tax Credit', 'GST Reconciliation', 'GST Audit',
];

export const ACCOUNTING_FEATURES = [
  'TallyPrime', 'Voucher Generation', 'Ledger Mapping', 'Journal Entries',
  'Posting Queue', 'Validation', 'Rollback', 'Audit Trail',
];

export const RECONCILIATION_TYPES = [
  'Settlement Reconciliation', 'Order Reconciliation', 'Refund Reconciliation',
  'Advertising Reconciliation', 'GST Reconciliation', 'Bank Reconciliation',
  'Inventory Reconciliation', 'AI-assisted Matching',
];

export const CLAIM_TYPES = [
  'Lost Inventory', 'Damaged Inventory', 'Incorrect Fees', 'Reimbursements',
  'Recovery Tracking', 'Claim Recommendations',
];

export const FORECAST_METRICS = [
  'Revenue', 'Profit', 'Cash Flow', 'Working Capital', 'Inventory Investment',
  'Advertising Spend', 'Settlement', 'ROI',
];

export const CFO_CAPABILITIES = [
  'Financial Analysis', 'GST Guidance', 'Profit Optimization', 'Cash Flow Planning',
  'Risk Analysis', 'Scenario Planning', 'Investment Advice',
];

export const SCENARIO_TYPES = [
  'Pricing Changes', 'Advertising Changes', 'Inventory Changes', 'Supplier Changes',
  'Marketplace Fee Changes', 'Tax Changes', 'Business Expansion',
];

export const REPORT_TYPES = [
  'P&L', 'Balance Sheet', 'Cash Flow Statement', 'Trial Balance',
  'Ledger Reports', 'GST Reports', 'Executive Reports', 'Board Reports',
];

export const AUDIT_FEATURES = [
  'Financial Audit Trail', 'Approval History', 'User Activity', 'Marketplace Audit',
  'AI Decision Audit', 'Compliance Logs',
];

export const ANALYTICS_METRICS = [
  'Profit Trends', 'Margin Trends', 'Cash Flow Trends', 'Fee Trends',
  'Forecast Accuracy', 'Claim Recovery', 'Business Health',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Executive Financial Command Center', 'Settlement Intelligence', 'Fee Intelligence',
  'Profitability Engine', 'Cost Intelligence', 'Cash Flow Intelligence', 'GST Intelligence',
  'Accounting Integration', 'Reconciliation', 'Claims Management', 'Forecasting', 'AI CFO',
];

export const LIVE_ROUTES: Record<string, string> = {
  finIntel: '/finance/intelligence',
  financeOps: '/amazon/finance-ops',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Revenue', value: '₹48.2L', change: '+12%', positive: true },
  { label: 'Gross Profit', value: '₹22.4L', change: '+10%', positive: true },
  { label: 'Net Profit', value: '₹14.6L', change: '+8%', positive: true },
  { label: 'EBITDA', value: '₹16.8L', change: '+9%', positive: true },
  { label: 'Cash Position', value: '₹32.1L', change: '+5%', positive: true },
  { label: 'Working Capital', value: '₹68.4L', change: '-3%', positive: true },
  { label: 'Settlement Health', value: '96%', change: '+2pp', positive: true },
  { label: 'Business Health', value: '88/100', change: '+4', positive: true },
  { label: 'AI Confidence', value: '91%', change: 'stable', positive: true },
];

export const DEMO_SETTLEMENTS: SettlementRow[] = [
  { id: 'SET-2026-0728', expected: '₹12.4L', actual: '₹12.2L', variance: '-₹2K', status: 'reconciled' },
  { id: 'SET-2026-0721', expected: '₹11.8L', actual: '₹11.8L', variance: '₹0', status: 'reconciled' },
  { id: 'SET-2026-0714', expected: '₹10.2L', actual: '₹9.6L', variance: '-₹60K', status: 'review' },
];

export const DEMO_PROFITABILITY: ProfitabilityRow[] = [
  { level: 'ASIN', name: 'SW-EARBUDS-PRO', margin: '38%', roi: '4.2x' },
  { level: 'Brand', name: 'SoundWave', margin: '32%', roi: '3.8x' },
  { level: 'Marketplace', name: 'Amazon.in', margin: '28%', roi: '3.2x' },
  { level: 'Advertising', name: 'SP Exact Match', margin: '24%', roi: '5.1x' },
];

export const DEMO_FEES: FeeRow[] = [
  { type: 'Marketplace Fees', amount: '₹4.2L', pct: '8.7%', leak: false },
  { type: 'FBA Fees', amount: '₹2.8L', pct: '5.8%', leak: false },
  { type: 'Storage Fees', amount: '₹2.1L', pct: '4.4%', leak: true },
  { type: 'Advertising Cost', amount: '₹5.4L', pct: '11.2%', leak: false },
  { type: 'Returns Cost', amount: '₹1.2L', pct: '2.5%', leak: false },
];

export const DEMO_CASH_FLOW: CashFlowRow[] = [
  { label: 'Cash Position', current: '₹32.1L', forecast: '₹38.4L' },
  { label: 'Receivables', current: '₹18.2L', forecast: '₹14.6L' },
  { label: 'Payables', current: '₹12.4L', forecast: '₹10.8L' },
  { label: 'GST Liability', current: '₹3.8L', forecast: '₹4.2L' },
  { label: 'Liquidity Risk', current: 'Low', forecast: 'Low' },
];

export const DEMO_GST: GSTRow[] = [
  { return: 'GSTR-1', period: 'Jul 2026', status: 'filed', dueDate: 'Aug 11' },
  { return: 'GSTR-3B', period: 'Jul 2026', status: 'ready', dueDate: 'Aug 20' },
  { return: 'Input Tax Credit', period: 'Jul 2026', status: 'reconciled', dueDate: '—' },
];

export const DEMO_RECONCILIATIONS: ReconciliationRow[] = [
  { type: 'Settlement', matched: 98, exceptions: 2, status: 'review' },
  { type: 'Order', matched: 99, exceptions: 1, status: 'complete' },
  { type: 'GST', matched: 97, exceptions: 3, status: 'in_progress' },
  { type: 'Bank', matched: 100, exceptions: 0, status: 'complete' },
];

export const DEMO_CFO: CFORec[] = [
  { type: 'Profit Optimization', title: 'Reduce storage fees — liquidate 412 aging units', impact: '+₹1.8L/mo margin', confidence: 92 },
  { type: 'Cash Flow Planning', title: 'Delay PO-2026-0841 by 5 days — cash flow optimization', impact: 'Free ₹12.2L working capital', confidence: 88 },
  { type: 'GST Guidance', title: 'Claim ₹42K ITC mismatch before GSTR-3B filing', impact: 'Recover input tax credit', confidence: 94 },
  { type: 'Risk Analysis', title: 'Settlement variance SET-2026-0714 — investigate ₹60K gap', impact: 'Prevent recurring leakage', confidence: 86 },
  { type: 'Investment Advice', title: 'Increase ad spend on 5.1x ROAS campaigns by 12%', impact: '+₹2.4L net profit/mo', confidence: 81 },
];

export function buildFinancialIntelDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    settlement_features: SETTLEMENT_FEATURES.length,
    fee_types: FEE_TYPES.length,
    profitability_levels: PROFITABILITY_LEVELS.length,
    gst_features: GST_FEATURES.length,
    reconciliation_types: RECONCILIATION_TYPES.length,
  };
}
